import * as turf from "@turf/turf";

// The projects API expects a MultiPolygon boundary.
export function toMultiPolygon(features) {
	return {
		type: "MultiPolygon",
		coordinates: features.flatMap((f) =>
			f.geometry.type === "MultiPolygon"
				? f.geometry.coordinates
				: [f.geometry.coordinates]
		)
	};
}

// Union all features so adjacent/overlapping polygons dissolve into one.
export function dissolveToMultiPolygon(features) {
	try {
		const merged = features.reduce(
			(acc, f) => (acc == null ? f : turf.union(acc, f)),
			null
		);
		if (!merged) return toMultiPolygon(features);
		const g = merged.geometry;
		return g.type === "MultiPolygon"
			? g
			: { type: "MultiPolygon", coordinates: [g.coordinates] };
	} catch (e) {
		// Invalid/degenerate geometry — fall back to the plain collection
		return toMultiPolygon(features);
	}
}

const polygonsOf = (geometry) =>
	geometry.type === "MultiPolygon" ? geometry.coordinates : [geometry.coordinates];

// Shared area in m2. booleanOverlap also counts parts that only touch at a
// corner, which is a valid MultiPolygon.
function overlapArea(a, b) {
	try {
		const x = turf.intersect(a, b);
		return x ? turf.area(x) : 0;
	} catch (e) {
		return 0;
	}
}

// Points where a polygon outline crosses itself, and where the parts of a
// MultiPolygon overlap each other. Same rule as the project boundary tool
// (ToolBoundary.checkBoundary). Returns {kinks: [[lng, lat], ...], overlaps: n}.
export function findInvalidity(geometry) {
	const kinks = [];
	const parts = [];
	polygonsOf(geometry).forEach((coords) => {
		try {
			const poly = turf.polygon(coords);
			parts.push(poly);
			turf.kinks(poly).features.forEach((f) => kinks.push(f.geometry.coordinates));
		} catch (e) { /* fewer than 4 positions: nothing to cross yet */ }
	});
	let overlaps = 0;
	for (let i = 0; i < parts.length; i++)
		for (let j = i + 1; j < parts.length; j++)
			if (overlapArea(parts[i], parts[j]) > 1) overlaps++;
	return { kinks, overlaps };
}

// Splits self-intersecting polygons at their crossings and dissolves the
// pieces, so overlapping lobes become one area (cf. ToolBoundary.flatten).
export function fixSelfIntersections(geometry) {
	const pieces = [];
	polygonsOf(geometry).forEach((coords) => {
		const poly = turf.polygon(coords);
		if (turf.kinks(poly).features.length)
			turf.unkinkPolygon(poly).features.forEach((f) => pieces.push(f));
		else
			pieces.push(poly);
	});
	return dissolveToMultiPolygon(pieces);
}
